import React from 'react';
import { motion } from 'framer-motion';
import { Layout } from '../layout/Layout';
import { BarChart, Clock, Users, PieChart, TrendingUp, MessageSquare } from 'lucide-react';

const benefits = [
  {
    icon: Clock,
    title: '70% Snellere Responstijd',
    description: 'Directe antwoorden zonder wachttijden voor klanten.',
    stats: [
      { label: 'Gemiddelde responstijd', value: '< 10 sec' },
      { label: 'Beschikbaarheid', value: '24/7' }
    ]
  },
  {
    icon: Users,
    title: '35% Hogere Klanttevredenheid',
    description: 'Verbeterde klantervaring door consistente en accurate service.',
    stats: [
      { label: 'Klantentevredenheid', value: '95%' },
      { label: 'Terugkerende klanten', value: '+40%' }
    ]
  },
  {
    icon: BarChart,
    title: '50% Kostenbesparing',
    description: 'Significante verlaging van operationele kosten.',
    stats: [
      { label: 'ROI', value: '300%' },
      { label: 'Kostenreductie', value: '50%' }
    ]
  }
];

const testimonials = [
  {
    name: 'Lisa van der Berg',
    role: 'Customer Success Manager',
    company: 'TechCorp BV',
    content: 'De AI-chatbot heeft onze klantenservice compleet getransformeerd. We kunnen nu 24/7 ondersteuning bieden met consistente kwaliteit.'
  },
  {
    name: 'Mark Jansen',
    role: 'Operations Director',
    company: 'Retail Solutions',
    content: 'De implementatie was eenvoudig en de resultaten waren direct zichtbaar. Onze klanttevredenheid is significant gestegen.'
  },
  {
    name: 'Emma de Vries',
    role: 'Head of Support',
    company: 'E-commerce Plus',
    content: 'De intelligente automatisering heeft ons team in staat gesteld om zich te focussen op complexere klantvragen.'
  }
];

export function BenefitsPage() {
  return (
    <Layout>
      <div className="bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-brand-50 text-brand-600 px-4 py-1.5 rounded-full font-medium mb-6"
            >
              <TrendingUp size={20} />
              Meetbare Resultaten
            </motion.div>
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Bewezen Voordelen voor Uw Bedrijf
            </h1>
            <p className="text-xl text-gray-600">
              Ontdek hoe onze AI-oplossing concrete resultaten levert en uw klantenservice naar een hoger niveau tilt.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mb-6">
                  <benefit.icon className="w-6 h-6 text-brand-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {benefit.description}
                </p>
                <div className="space-y-3">
                  {benefit.stats.map((stat, statIndex) => (
                    <div key={statIndex} className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">{stat.label}</span>
                      <span className="text-lg font-semibold text-brand-600">{stat.value}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="bg-gray-50 rounded-2xl p-8 mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Wat Onze Klanten Zeggen</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-xl shadow-sm"
                >
                  <p className="text-gray-600 mb-4">{testimonial.content}</p>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                    <p className="text-sm text-brand-600">{testimonial.company}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Ervaar de Voordelen Zelf
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Begin vandaag nog met het optimaliseren van uw klantenservice met onze AI-oplossing.
            </p>
            <button className="bg-brand-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-brand-700 transition-colors">
              Start Gratis Proefperiode
            </button>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}